import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ImageGallery = ({ isOpen, onClose, images = [], initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  const handlePrevious = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, onClose]);

  if (!isOpen || images.length === 0) return null;

  const formatIndex = (num) => (num + 1).toString().padStart(2, '0');

  return createPortal(
    <div
      className="fixed inset-0 bg-black/95 z-[99999] flex flex-col items-center justify-center p-4 transition-all duration-300"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-[110]"
      >
        <X className="w-6 h-6" />
      </button>

      <div
        className="relative w-full h-[calc(100vh-160px)] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`Gallery image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain shadow-2xl transition-transform duration-300 pointer-events-auto"
        />
      </div>

      <div
        className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-[#0a1a14]/90 backdrop-blur-md border border-white/5 disabled rounded-full px-8 py-3 flex items-center gap-8 z-[110] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handlePrevious}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
        >
          <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-bold uppercase tracking-widest">Prev</span>
        </button>

        <div className="text-white font-medium text-sm tracking-widest border-x border-white/10 px-8">
          {formatIndex(currentIndex)} / {formatIndex(images.length)}
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
        >
          <span className="text-xs font-bold uppercase tracking-widest">Next</span>
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ImageGallery;
